import React, { useState, useEffect } from 'react';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem, TextField,
  Typography, Alert, Card, Grid, Avatar, IconButton, Paper, Chip
} from '@mui/material';
import {
  DatePicker, LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarToday, Person as PersonIcon, UploadFile, Close, EventNote
} from '@mui/icons-material';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import { enGB } from 'date-fns/locale';
import {
  createLeave,
  getLeaveBalance,
  getEmployeeLeaveBalance,
  createLeaveById,
  addLeaveJustificationByHR,
  addLeaveJustification
} from '../../services/leavesAPIs';
import LeaveCalendar from '../../components/calendars/CalendarCard';
import LeaveBalanceCard from '../../components/cards/BalanceCard';
import { getEmployeeImage } from '../../services/HRstaff';
import { getMyImage } from '../../services/EmployeeService';
import { getUpcamingBlocked } from '../../services/timeOverviewAPIs';

const LeaveRequestForm = ({
  lb,
  id,
  onSubmitSuccess,
  employees,
  IsEmployee,
  fetchData
}) => {
  const [formData, setFormData] = useState({
    type: '',
    startDate: null,
    endDate: null,
    justification: '',
  });
  const [justificationFile, setJustificationFile] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState({ annual: 20, unpaid: 10, maternity: 90 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(null);

  // blocking events:
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [overlappingEvent, setOverlappingEvent] = useState(null);

  useEffect(() => {
    const fetchBlockedPeriods = async () => {
      try {
        const blocked = await getUpcamingBlocked();
        setBlockedPeriods(blocked || []);
      } catch (err) {
        console.error('Failed to fetch blocked periods:', err);
      }
    };
    fetchBlockedPeriods();
  }, []);

  const isDateRangeOverlapping = (start1, end1, start2, end2) => {
    const s1 = new Date(start1);
    const e1 = new Date(end1);
    const s2 = new Date(start2);
    const e2 = new Date(end2);
    return s1 <= e2 && s2 <= e1;
  };

  // Confirmation Dialog
  const handleConfirmSubmit = () => {
    setConfirmationDialogOpen(false);
    submitLeaveRequest();
  };

  const handleCancelSubmit = () => {
    setConfirmationDialogOpen(false);
    setOverlappingEvent(null);
  };

  // === FETCH IMAGE FOR LOGGED-IN EMPLOYEE ===
  useEffect(() => {
    const fetchImage = async () => {
      if (IsEmployee) {
        try {
          const url = await getMyImage();
          setProfileImageUrl(url);
        } catch (err) {
          console.error('Failed to load image:', err);
          setProfileImageUrl(null);
        }
      } else {
        setProfileImageUrl(null);
      }
    };
    fetchImage();
  }, [IsEmployee]);

  // === FETCH IMAGE FOR SELECTED EMPLOYEE (HR VIEW) ===
  useEffect(() => {
    const fetchImage = async () => {
      if (!IsEmployee && id && employees) {
        const emp = employees.find(e => e.employeeId === id);
        if (emp?.image) {
          try {
            const url = await getEmployeeImage(id);
            setProfileImageUrl(url);
          } catch (err) {
            console.error('Failed to load image:', err);
            setProfileImageUrl(null);
          }
        } else {
          setProfileImageUrl(null);
        }
      }
    };
    fetchImage();
  }, [id, employees, IsEmployee]);

  // Cleanup image URL
  useEffect(() => {
    return () => {
      if (profileImageUrl) URL.revokeObjectURL(profileImageUrl);
    };
  }, [profileImageUrl]);

  // Fetch leave balance
  const fetchLeaveBalance = async () => {
    // Prevent calling API with null id when handleWithEmployeeId is active
    if (lb === 'handleWithEmployeeId' && !id) {
      setLeaveBalance(null);
      return;
    }

    try {
      let data;
      if (lb === 'handleWithEmployeeId') {
        data = await getEmployeeLeaveBalance(id);
      } else {
        data = await getLeaveBalance();
      }
      setLeaveBalance(data);
      console.log('Fetched Leave Balance (HR/List View):', data);
    } catch (error) {
      console.error('Failed to fetch leave balance', error);
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, [id, lb]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Start date: normal behaviour
  const handleStartDateChange = (date) => {
    setError('');
    setFormData(prev => ({
      ...prev,
      startDate: date,
      // if start date changes, reset end date
      endDate: prev.endDate && date && prev.endDate < date ? null : prev.endDate
    }));
  };

  // End date: normal behaviour
  const handleEndDateChange = (date) => {
    setError('');
    setFormData(prev => ({
      ...prev,
      endDate: date
    }));
  };

  // Calendar selection: same logic as pickers
  const handleCalendarDateSelect = (date) => {
    setError('');
    if (!formData.startDate || (formData.startDate && formData.endDate)) {
      setFormData(prev => ({ ...prev, startDate: date, endDate: null }));
    } else if (formData.startDate && !formData.endDate) {
      if (date >= formData.startDate) {
        setFormData(prev => ({ ...prev, endDate: date }));
      } else {
        setFormData(prev => ({ ...prev, startDate: date, endDate: null }));
      }
    }
  };

  const selectedEmployee = employees?.find(emp => emp.employeeId === id);
  const employeeName = selectedEmployee
    ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : id ? `Employee ID: ${id}` : 'Select an employee';

  const validateForm = () => {
    if (!id && lb === 'handleWithEmployeeId') {
      setError('Please select an employee.');
      return false;
    }
    if (!formData.type || !formData.startDate || !formData.endDate || !formData.justification) {
      setError('All fields are required');
      return false;
    }

    // Year validation - only current, previous, or next year
    const currentYear = new Date().getFullYear();
    const startYear = formData.startDate.getFullYear();
    const endYear = formData.endDate.getFullYear();

    if (startYear < currentYear - 1 || startYear > currentYear + 1) {
      setError(`Invalid start date year. Please select a date from ${currentYear - 1}, ${currentYear}, or ${currentYear + 1}.`);
      return false;
    }

    if (endYear < currentYear - 1 || endYear > currentYear + 1) {
      setError(`Invalid end date year. Please select a date from ${currentYear - 1}, ${currentYear}, or ${currentYear + 1}.`);
      return false;
    }

    if (IsEmployee && formData.startDate < new Date()) {
      setError('Start date cannot be in the past');
      return false;
    }
    if (formData.endDate < formData.startDate) {
      setError('End date cannot be before start date');
      return false;
    }
    return true;
  };

  const formatDateOnly = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    // Check for overlap with blocked periods
    const formattedStartDate = formatDateOnly(formData.startDate);
    const formattedEndDate = formatDateOnly(formData.endDate);

    const overlapping = blockedPeriods.find(event =>
      isDateRangeOverlapping(formattedStartDate, formattedEndDate, event.startDate, event.endDate)
    );

    if (overlapping) {
      setOverlappingEvent(overlapping);
      setConfirmationDialogOpen(true);
      return;
    }

    // If no overlap, submit directly
    await submitLeaveRequest();
  };

  const submitLeaveRequest = async () => {
    const FD = {
      ...formData,
      startDate: formatDateOnly(formData.startDate),
      endDate: formatDateOnly(formData.endDate),
    };

    try {
      let createdLeave;
      if (lb === 'handleWithEmployeeId' && id) {
        const payload = {
          ...FD,
          employee: { employeeId: id },
        };
        createdLeave = await createLeaveById(payload);
      } else {
        createdLeave = await createLeave(FD);
      }

      // Upload justification file if needed
      if (formData.type === 'UNPAID_LEAVE' && justificationFile) {
        try {
          if (IsEmployee) {
            await addLeaveJustification(createdLeave, justificationFile);
          } else {
            await addLeaveJustificationByHR(createdLeave, justificationFile);
          }
        } catch (uploadErr) {
          console.error('Justification upload failed:', uploadErr);
          setError('Leave created, but file upload failed. Please retry.');
        }
      }

      setSuccess('Leave request submitted successfully');
      setFormData({ type: '', startDate: null, endDate: null, justification: '' });
      setJustificationFile(null);
      fetchData?.();
      await fetchLeaveBalance();
      if (onSubmitSuccess) {
        setTimeout(() => onSubmitSuccess(), 500);
      }
    } catch (err) {
      const msg = err.response?.data;
      if (msg === 'Leave request conflicts with existing request') {
        setError('You already have a leave during this period.');
      } else if (msg === "Employees with an Internship contract cannot request annual leave.") {
        setError('Employees with an Internship contract cannot request annual leave.');
      } else if (msg === 'Insufficient leave balance') {
        setError('You do not have enough leave balance for this request.');
      } else if (msg === 'Start date cannot be in the past') {
        setError('Start date cannot be in the past');
      } else {
        setError('Failed to submit leave request. Please try again.');
      }
      console.error('Submission error:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJustificationFile(file);
    }
  };

  const removeFile = () => {
    setJustificationFile(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'transparent',
        p: 3
      }}>
        <Grid sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} container spacing={3} >
          {/* Leave Balance Card - Full Width */}
          <LeaveBalanceCard leaveBalance={leaveBalance} />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
            {/* Main Form Card */}
            <Grid sx={{ width: '63%' }} item xs={12} md={7}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  overflow: 'visible',
                  background: 'var(--card-bg, white)',
                  height: '100%',
                  width: '100%'
                }}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #7091e6 0%, #3d52a0 100%)',
                  p: 3,
                  color: 'white',
                  borderRadius: 2,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        border: '3px solid rgba(255,255,255,0.3)',
                        fontWeight: 'bold',
                        fontSize: '1.3rem',
                      }}
                    >
                      {profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt="Employee"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : selectedEmployee ? (
                        `${selectedEmployee.firstName[0]}${selectedEmployee.lastName[0]}`.toUpperCase()
                      ) : (
                        <PersonIcon sx={{ fontSize: 28 }} />
                      )}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" fontWeight={700}>
                        Leave Request
                      </Typography>
                      {id && (
                        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                          {employeeName}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      icon={<EventNote />}
                      label="Leave"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ p: 3 }}>
                  {/* Alerts */}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)'
                      }}
                    >
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)'
                      }}
                    >
                      {success}
                    </Alert>
                  )}

                  {/* Form */}
                  <Box component="form" onSubmit={handleSubmit}>
                    {/* Leave Type */}
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>Leave Type</InputLabel>
                        <Select
                          name="type"
                          value={formData.type}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          label="Leave Type"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="ANNUAL_LEAVE">Annual Leave</MenuItem>
                          <MenuItem value="UNPAID_LEAVE">Unpaid Leave</MenuItem>
                          <MenuItem value="MATERNITY_LEAVE">Maternity Leave</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Start Date */}
                    <Box sx={{ mb: 3 }}>
                      <DatePicker
                        label="Start Date"
                        value={formData.startDate}
                        onChange={handleStartDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { borderRadius: 2 }
                          }
                        }}
                        format="dd/MM/yyyy"
                        minDate={IsEmployee ? new Date() : undefined}
                      />
                    </Box>

                    {/* End Date */}
                    <Box sx={{ mb: 3 }}>
                      <DatePicker
                        label="End Date"
                        value={formData.endDate}
                        onChange={handleEndDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { borderRadius: 2 }
                          }
                        }}
                        format="dd/MM/yyyy"
                        minDate={formData.startDate || (IsEmployee ? new Date() : undefined)}
                      />
                    </Box>

                    {/* Justification */}
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        name="justification"
                        label="Justification"
                        value={formData.justification}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ borderRadius: 2 }}
                        placeholder="Please provide details about your leave request..."
                      />
                    </Box>

                    {/* File Upload (Unpaid Leave) */}
                    {formData.type === 'UNPAID_LEAVE' && (
                      <Box sx={{ mb: 3 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<UploadFile />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            py: 1.5,
                            borderColor: '#667eea',
                            color: '#667eea',
                            '&:hover': { borderColor: '#764ba2', bgcolor: '#f8f9fa' }
                          }}
                        >
                          {justificationFile ? justificationFile.name : 'Upload Justification Document'}
                          <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                        </Button>
                        {justificationFile && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {justificationFile.name}
                            </Typography>
                            <IconButton size="small" onClick={removeFile}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Submit Button */}
                    <Box>
                      <Button
                        variant="contained"
                        type="submit"
                        fullWidth
                        size="large"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          py: 1.5,
                          fontSize: '1rem',
                          background: 'linear-gradient(135deg, #7091e6 0%, #3d52a0 100%)',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                          },
                        }}
                      >
                        Submit Leave Request
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Calendar Card */}
            <Grid sx={{ width: '35%' }} item xs={12} md={5}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  minHeight: 400,
                  background: 'var(--card-bg, white)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box sx={{
                  p: 2.5,
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12
                }}>
                  <Typography variant="h6" fontWeight={700} color="#333">
                    <CalendarToday sx={{ verticalAlign: 'middle', mr: 1, mb: 0.5 }} />
                    Select Dates
                  </Typography>
                </Box>

                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <LeaveCalendar
                    startDate={formData.startDate}
                    endDate={formData.endDate}
                    onDateSelect={handleCalendarDateSelect}
                    highlightRange
                  />
                </Box>

                <Paper sx={{ m: 3, mt: 0, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                    SELECTED DATES
                  </Typography>
                  {formData.startDate ? (
                    <>
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 1 }}>
                        From: {formData.startDate.toLocaleDateString('en-GB')}
                      </Typography>
                      {formData.endDate && (
                        <Typography variant="body2" fontWeight={500}>
                          To: {formData.endDate.toLocaleDateString('en-GB')}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      No dates selected yet
                    </Typography>
                  )}
                </Paper>
              </Card>
            </Grid>
          </Box>
        </Grid>
      </Box>

      {/* Conflict Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCancelSubmit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: "form-modal" }}
      >
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>
          ⚠️ Conflicting Event Detected
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your requested leave period overlaps with a scheduled event:
          </DialogContentText>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              {overlappingEvent?.title || 'Blocked Period'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Dates:</strong> {overlappingEvent?.startDate} to {overlappingEvent?.endDate}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              <strong>Description:</strong> {overlappingEvent?.description || 'No description provided.'}
            </Typography>
          </Paper>

          <DialogContentText>
            <strong>Warning:</strong> This period is marked as <strong>BLOCKED</strong>.
            Submitting a leave request during this time may result in rejection if your presence is required.
            Please confirm only if you are certain you can be absent during this event.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubmit} color="secondary">
            Cancel Request
          </Button>
          <Button onClick={handleConfirmSubmit} variant="contained" color="error" autoFocus>
            Confirm &amp; Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default LeaveRequestForm;
